import { useState } from "react";

/**
 * The debug-only forced view states, in one place.
 *
 * Both of these exist so every terminal/pending UI state can be previewed without waiting on
 * the real lifecycle — a resolver call takes minutes, needs a past resolution date, and a
 * branch premise additionally needs a minted trigger contract with a graded verdict. Without
 * these you cannot see most of the resolution surface at all during development.
 *
 * ── They are VIEW-LAYER ONLY ────────────────────────────────────────────────
 * Neither is ever written anywhere. `resolution` is threaded into the lane's verdict chain
 * and `premise` into `premiseViewsFor`, both of which are pure derivations — so an override
 * cannot mutate a forecast record, cannot persist, and cannot reach the data plane. That is
 * what makes it safe to force states that the data could not currently produce.
 *
 * Lives in a hook rather than as two `useState` calls in `AppInner` because the state
 * architecture ratchet counts those, and a debug affordance is exactly the kind of thing that
 * should not buy itself a slot in the orchestrator. See chatboard/CLAUDE.md → State
 * architecture.
 *
 * @returns {{
 *   resolution: string|null, setResolution: Function,
 *   premise: string|null, setPremise: Function,
 *   generating: boolean, setGenerating: Function,
 *   clearAll: Function,
 * }}
 */
export function useDebugOverrides() {
  // Forced verdict for the MAIN forecast — "resolved_yes" | "resolved_no" | "unresolved".
  // Drives both the binary resolution pie and, under All Multiway, the winning-bar collapse
  // (the lane feeds it through the same `distStatus` the real verdict uses).
  const [resolution, setResolution] = useState(null);
  // Forced BRANCH premise state — see branchPremise.PremiseOverride. Applies to every branch
  // on the lane at once, which is the point: the grid of states is what you want to look at.
  const [premise, setPremise] = useState(null);
  // Force the lane into its GENERATING/awaiting state. Without this the "Generating
  // Forecast" sweep and the dash gap cut for its label are only visible during a real
  // in-flight forecast — a window of seconds you cannot pause — so the one part of the
  // resolution surface that is pure layout was also the only part that could not be
  // inspected. Pairs with the two above: this is the PENDING state of the main forecast.
  const [generating, setGenerating] = useState(false);

  /** Drop both overrides — the "back to reality" escape hatch. */
  const clearAll = () => {
    setResolution(null);
    setPremise(null);
    setGenerating(false);
  };

  return { resolution, setResolution, premise, setPremise, generating, setGenerating, clearAll };
}
